import { html, nothing, TemplateResult } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { environment } from '../../environments/environment';
import { MegaMenuTemplateProps, MenuItem, RightAction } from '../../shared/types';
import { renderDesktContainer } from '../render-options/desktop.template';
import { renderMobileContainer } from '../render-options/mobile.template';
import './user-area/user-area';

/* ------------------------------------------------------------------
 * Helper render functions
 * ------------------------------------------------------------------*/
const renderCoreOption = (
  item: MenuItem,
  idx: number,
  props: MegaMenuTemplateProps
): TemplateResult => {
  const { project, selectedOption } = props;
  const isSelected = props.selectedOption?.title === item.title;
  const id = ['inmobiliario', 'movilidad', 'compras'][idx] ?? `opt-${idx}`;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      props.onCoreSelect(idx, id, 'desktop');
    }
  };

  return html`
    <li
      id=${id}
      class=${classMap({
        'core-option': true,
        selected: (!selectedOption && project === id) || isSelected
      })}
      role="button"
      tabindex="0"
      aria-label=${`Ir a Tu360 ${item.title}`}
      @click=${() => props.onCoreSelect(idx, id, 'desktop')}
      @keydown=${handleKeyDown}
    >
      <div class="core-option-logo bc-flex bc-justify-content-center bc-align-items-center">
        <img src="${environment.assetsUrl}/images/${id}.svg" alt=${item.title} aria-hidden="true" />
      </div>
    </li>
  `;
};

const renderCategoryList = (props: MegaMenuTemplateProps) => {
  const cat = props.selectedCategory;
  return html`
    <section
      id="submenu-right"
      class="submenu-container-right bc-p-4 ${classMap({
        'submenu-container-right--active': !!cat
      })}"
    >
      <div class="bc-flex bc-justify-content-space-between">
        <h2
          class="bc-mt-2 bc-mb-3 title"
          style=${styleMap({ '--color': props.selectedOption?.brandColor ?? '#000' })}
        >
          ${cat?.title}
        </h2>
        <span
          class="bc-flex bc-align-items-center bc-gap-2 exit"
          role="button"
          tabindex="0"
          aria-label="Cerrar menú"
          @click=${props.onClose}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              props.onClose();
            }
          }}
          >Cerrar<em class="bc-icon bc-icon-button" aria-hidden="true">error</em></span
        >
      </div>

      <div class="bc-flex category-options-container">
        ${repeat(
          cat?.listOptions ?? [],
          o => o.title,
          option => html`
            <div class="category-option bc-flex bc-direction-column">
              <h3 class="category-option__title">${option.title}</h3>
              ${repeat(
                option.options ?? [],
                i => i.title,
                item => html`
                  <a
                    class="category-option__item"
                    tabindex="0"
                    @click=${(e: MouseEvent) => props.onLeafClick(e, item, option.title)}
                    href="${item.url}"
                    >${item.title}</a
                  >
                `
              )}
            </div>
          `
        )}
      </div>
      ${!cat?.actionButtonInfo?.title && !cat?.actionButtonInfo?.url
        ? nothing
        : html`
            <a
              class="more-info bc-mt-3 bc-flex bc-align-items-center bc-gap-2"
              href="${cat.actionButtonInfo.url}"
              tabindex="0"
              aria-label="${cat.actionButtonInfo.title}"
              target="_blank"
              rel="noopener noreferrer"
              @click=${(e: MouseEvent) =>
                props.onActionButtonClick(e, cat.actionButtonInfo, cat.title)}
            >
              <span>${cat.actionButtonInfo.title}</span>
              <i class="bc-icon-button bc-icon" aria-hidden="true">arrow-long-right</i>
            </a>
          `}
    </section>
  `;
};

const renderHeaderAction = (item: RightAction, props: MegaMenuTemplateProps): TemplateResult => {
  if (item.id !== 'login') {
    return html`
      <li id=${item.id} class="header-action bc-flex bc-align-items-center bc-gap-2">
        <a
          class="bc-flex bc-align-items-center bc-gap-2"
          href="${item.url}"
          tabindex="0"
          aria-label="${item.label}"
          @click=${(e: MouseEvent) => props.onRigthClick(e, item)}
        >
          <em class="bc-icon-button bc-icon" aria-hidden="true">${item.icon}</em>
          <span>${item.label}</span>
        </a>
      </li>
    `;
  }

  // Si showLoginButton es false, no renderizar el botón de login
  if (!props.showLoginButton) {
    return html``;
  }

  if (!props.userName?.length) {
    return html`
      <li id=${item.id} class="header-action bc-flex bc-align-items-center bc-gap-2">
        <a
          class="bc-flex bc-align-items-center bc-gap-2"
          href="#"
          tabindex="0"
          aria-label="${item.label}"
          @click=${(e: MouseEvent) => props.onRigthClick(e, item)}
        >
          <em class="bc-icon-button bc-icon" aria-hidden="true">${item.icon}</em>
          <span>${item.label}</span>
        </a>
      </li>
    `;
  }
  return html`
    <li id="auth" class="header-action bc-flex bc-align-items-center bc-gap-2">
      <mm-user-area
        .authenticated=${props.authenticated}
        .userName=${props.userName}
        .authItems=${props.userMenuItems}
        @login=${props.onLogin}
        @user-select=${(e: CustomEvent) => props.onUserMenuSelect(e.detail)}
      ></mm-user-area>
    </li>
  `;
};

/* ------------------------------------------------------------------
 * Template principal
 * ------------------------------------------------------------------*/
export const megaMenuTemplate = (props: MegaMenuTemplateProps) => {
  const isCompactViewport = props.isCompactViewport;
  const id = props.project;
  const isSubmenuOpen = props.submenuOpen;
  let borderRadius = '1rem';
  if (isCompactViewport) {
    borderRadius = '0';
  } else if (props.selectedCategory) {
    borderRadius = '1rem 0 0 1rem';
  } else {
    borderRadius = '1rem';
  }
  return html`
    <nav class="mega-menu-principal-container bc-flex bc-justify-content-space-between">
      <ul id="core-options" class="core-options-container bc-flex bc-align-items-end">
        <li class="core-option">
          ${!isCompactViewport
            ? html`
                <a href="https://${environment.urlTu360}">
                  <em
                    class="icon bc-icon bc-flex bc-justify-content-center bc-align-items-center"
                    aria-hidden="true"
                  >
                    isotipo-bancolombia
                  </em>
                </a>
              `
            : html`
                <a
                  href="/"
                  class="bc-flex bc-justify-content-center bc-align-items-center"
                  style=${styleMap({ display: isSubmenuOpen ? 'none' : 'block' })}
                >
                  <img src="${environment.assetsUrl}/images/${id}.svg" alt=${id} />
                </a>
              `}
        </li>
        ${!isCompactViewport
          ? repeat(
              props.menuItems,
              i => i.title,
              (item, idx) => renderCoreOption(item, idx, props)
            )
          : nothing}
      </ul>
      <ul class="right-actions bc-flex bc-align-items-center">
        ${!isSubmenuOpen
          ? repeat(
              props.rightActions,
              a => a.id,
              item => renderHeaderAction(item, props)
            )
          : nothing}
        <li
          id="mobile-nav"
          class="header-action bc-flex bc-align-items-center bc-gap-2"
          role="button"
          tabindex="0"
          aria-label="${isSubmenuOpen ? 'Cerrar menú' : 'Abrir menú'}"
          aria-expanded="${isSubmenuOpen}"
          @click=${() => props.onOpenNav()}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              props.onOpenNav();
            }
          }}
        >
          ${isSubmenuOpen ? html`<span>Cerrar</span>` : nothing}
          <em class="bc-icon-button bc-icon" aria-hidden="true"
            >${isSubmenuOpen ? 'error' : 'menu-bar'}</em
          >
        </li>
      </ul>
    </nav>

    <section
      id="submenu"
      class=${classMap({
        'submenu-container': true,
        'bc-flex': true,
        'submenu-container--active': props.submenuOpen
      })}
    >
      <section
        id="submenu-left"
        class="submenu-container-left bc-flex bc-direction-column bc-justify-content-space-between"
        style=${styleMap({ 'border-radius': borderRadius })}
      >
        ${!isCompactViewport ? renderDesktContainer(props) : renderMobileContainer(props)}
        <a href="https://${environment.urlTu360}" tabindex="0" aria-label="Ir a Tu360">
          <h3 class="submenu-option">
            Ir a <img src="${environment.assetsUrl}/images/tu360.svg" alt="Tu360" />
          </h3>
        </a>
      </section>

      ${!isCompactViewport ? renderCategoryList(props) : nothing}
    </section>
  `;
};
